"use client";

import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";

export interface CartQuantityStepperProps {
  name: string;
  quantity: number;
  minQuantity?: number;
  maxQuantity?: number;
  onChange: (quantity: number) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Bare -/input/+ quantity stepper. Typed edits are buffered locally and only
 * committed via `onChange` on blur/Enter, so clearing the field to type a
 * fresh number doesn't get clobbered by an intermediate invalid value.
 */
export function CartQuantityStepper({
  name,
  quantity,
  minQuantity = 1,
  maxQuantity,
  onChange,
  disabled,
  className,
}: CartQuantityStepperProps) {
  const max = maxQuantity ?? Infinity;
  const [draft, setDraft] = useState(String(quantity));

  useEffect(() => {
    setDraft(String(quantity));
  }, [quantity]);

  function commitDraft() {
    const parsed = Math.round(Number(draft));
    const clamped = Number.isFinite(parsed)
      ? Math.min(max, Math.max(minQuantity, parsed))
      : quantity;
    setDraft(String(clamped));
    if (clamped !== quantity) onChange(clamped);
  }

  function handleMinus() {
    const next = Math.max(minQuantity, quantity - 1);
    if (next !== quantity) onChange(next);
  }

  function handlePlus() {
    const next = Math.min(max, quantity + 1);
    if (next !== quantity) onChange(next);
  }

  return (
    <div
      className={cn(
        "flex h-10 shrink-0 items-center rounded-xl border border-border bg-muted p-1",
        className,
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-full w-8 shrink-0"
        onClick={handleMinus}
        disabled={disabled || quantity <= minQuantity}
        aria-label={`Remove one ${name}`}
      >
        <Minus className="h-4 w-4" />
      </Button>
      <Input
        type="number"
        min={minQuantity}
        max={maxQuantity}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
        disabled={disabled}
        aria-label={`${name} quantity`}
        className="h-full w-12 shrink-0 border-0 bg-transparent p-0 text-center shadow-none focus-visible:ring-0"
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-full w-8 shrink-0"
        onClick={handlePlus}
        disabled={disabled || quantity >= max}
        aria-label={`Add one more ${name}`}
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
}
